import { useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Loader2, AlertTriangle, CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Professional {
  id: string;
  full_name: string | null;
  total_patients: number;
  total_documents: number;
  total_results: number;
}

interface BulkDeletePatientsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professionals: Professional[];
  onSuccess: () => void;
}

export function BulkDeletePatientsModal({
  open,
  onOpenChange,
  professionals,
  onSuccess
}: BulkDeletePatientsModalProps) {
  const [confirmation, setConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState<{
    patients: number;
    documents: number;
    lab_results: number;
    health_records: number;
    files: number;
    total_deleted: number;
  } | null>(null);

  const totalProfessionals = professionals.length;
  const totalPatients = professionals.reduce((sum, p) => sum + p.total_patients, 0);
  const totalDocuments = professionals.reduce((sum, p) => sum + p.total_documents, 0);
  const totalResults = professionals.reduce((sum, p) => sum + p.total_results, 0);

  const confirmationText = `EXCLUIR ${totalPatients} PACIENTES`;
  const isConfirmed = confirmation === confirmationText;

  const handleBulkDeletePatients = async () => {
    if (!isConfirmed) return;

    try {
      setIsDeleting(true);
      setProgress(10);

      const professionalIds = professionals.map(p => p.id);

      setProgress(30);

      const { data, error } = await supabase.functions.invoke('delete-patients-bulk', {
        body: { professionalIds }
      });

      setProgress(90);

      if (error) throw error;

      setProgress(100);
      setStats(data.stats);

      toast.success('Pacientes excluídos com sucesso', {
        description: `${data.stats.total_deleted} registros removidos permanentemente`
      });

      setTimeout(() => {
        onSuccess();
        onOpenChange(false);
        setConfirmation("");
        setStats(null);
        setProgress(0);
      }, 3000);

    } catch (error) {
      console.error('Erro ao excluir pacientes:', error);
      toast.error('Erro ao excluir pacientes', {
        description: error instanceof Error ? error.message : 'Erro desconhecido'
      });
      setProgress(0);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
            <AlertDialogTitle>Excluir Pacientes de Profissionais Selecionados</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-4 pt-4">
            {stats ? (
              <Alert className="border-green-500 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold text-green-900">✅ Exclusão de pacientes concluída com sucesso!</p>
                    <div className="text-sm space-y-1 text-green-800">
                      <p>• {stats.patients} pacientes</p>
                      <p>• {stats.documents} documentos</p>
                      <p>• {stats.lab_results} resultados de exames</p>
                      <p>• {stats.health_records} registros de saúde</p>
                      <p>• {stats.files} arquivos do storage</p>
                      <p className="pt-2 font-bold">Total: {stats.total_deleted} registros excluídos</p>
                    </div>
                    <p className="text-sm text-green-700 mt-2">
                      ✅ Os profissionais foram mantidos no sistema
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-3 p-4 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <p className="font-semibold text-orange-900 dark:text-orange-100">
                    Você está prestes a excluir APENAS os pacientes de {totalProfessionals} profissional(is):
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-orange-600" />
                      <span className="font-medium">{totalPatients} pacientes</span>
                    </div>
                    <div className="pl-6 space-y-1 text-orange-800 dark:text-orange-200">
                      <p>• {totalDocuments} documentos</p>
                      <p>• {totalResults} resultados de exames</p>
                      <p>• Todos os registros de saúde associados</p>
                      <p>• Todos os arquivos do storage</p>
                    </div>
                  </div>
                </div>

                <Alert variant="destructive" className="bg-red-50 border-red-300">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-semibold">⚠️ ATENÇÃO - AÇÃO IRREVERSÍVEL</p>
                      <ul className="text-sm space-y-1 list-disc list-inside">
                        <li>Esta ação irá excluir PERMANENTEMENTE todos os pacientes dos profissionais selecionados</li>
                        <li>Os PROFISSIONAIS NÃO serão excluídos, apenas seus pacientes e dados relacionados</li>
                        <li>Documentos, exames, registros de saúde e arquivos serão removidos</li>
                        <li>Esta ação NÃO pode ser desfeita</li>
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="confirmation" className="text-sm font-medium">
                    Para confirmar, digite: <span className="font-mono text-destructive">{confirmationText}</span>
                  </Label>
                  <Input
                    id="confirmation"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder={confirmationText}
                    disabled={isDeleting}
                    className="font-mono"
                  />
                </div>

                {isDeleting && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Excluindo pacientes...</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                )}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!stats && (
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeletePatients}
              disabled={!isConfirmed || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                `Excluir ${totalPatients} Pacientes`
              )}
            </Button>
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
